import { Box, Stepper, Step, StepLabel, Tooltip, Typography } from "@mui/material";
import { CheckCircle, Category, Place, Build, Map, ShoppingCart, Star, Summarize } from "@mui/icons-material";
import { cloneElement } from "react";
import { ScopeOfWorkStep } from "../context/scope-of-work.types";
import { colors } from "../../../../../ui/colors";

const COLORS = {
  orange: {
    base: colors.orange.base,
    100: "#FFE4CC",
    200: "#FFD4B3",
  },
  gray: {
    100: "#F3F4F6",
    200: "#E5E7EB",
    300: "#D1D5DB",
    400: "#9CA3AF",
  },
};

export interface StepConfig {
  id: ScopeOfWorkStep;
  label: string;
  description: string;
}

interface VerticalStepperProps {
  steps: StepConfig[];
  currentStep: ScopeOfWorkStep;
  onStepClick?: (stepId: ScopeOfWorkStep) => void;
}

export const VerticalStepper = ({ steps, currentStep, onStepClick }: VerticalStepperProps) => {
  const activeStepIndex = steps.findIndex((s) => s.id === currentStep);

  const iconMap: Record<ScopeOfWorkStep, React.ReactNode> = {
    [ScopeOfWorkStep.Categories]: <Category />,
    [ScopeOfWorkStep.TypeOfPlaces]: <Place />,
    [ScopeOfWorkStep.TypeOfService]: <Build />,
    [ScopeOfWorkStep.Areas]: <Map />,
    [ScopeOfWorkStep.Objects]: <ShoppingCart />,
    [ScopeOfWorkStep.SpecialRequests]: <Star />,
    [ScopeOfWorkStep.Summary]: <Summarize />,
  };

  return (
    <Box
      sx={{
        ml: 5,
        width: 150,
        borderRadius: 3,
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        height: "fit-content",
      }}
    >
      <Stepper
        activeStep={activeStepIndex}
        orientation="vertical"
        sx={{
          "& .MuiStepConnector-line": {
            borderColor: COLORS.orange[200],
            borderWidth: 2,
          },
        }}
      >
        {steps.map((step, index) => {
          const completed = index < activeStepIndex;
          const isActive = index === activeStepIndex;

          const icon = completed ? (
            <CheckCircle sx={{ fontSize: 18 }} />
          ) : (
            cloneElement(iconMap[step.id] as React.ReactElement, {
              sx: { fontSize: 18 },
            })
          );

          return (
            <Step key={step.id} onClick={() => onStepClick?.(step.id)} sx={{ cursor: "pointer" }}>
              <Tooltip title={step.description} arrow>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: completed ? COLORS.orange[100] : isActive ? COLORS.orange.base : COLORS.gray[100],
                        color: completed ? COLORS.orange.base : isActive ? "#fff" : COLORS.gray[400],
                        border: `2px solid ${completed ? COLORS.orange[200] : isActive ? COLORS.orange.base : COLORS.gray[200]}`,
                      }}
                    >
                      {icon}
                    </Box>
                  )}
                >
                  <Typography
                    variant="body2"
                    fontWeight={isActive || completed ? 600 : 400}
                    sx={{
                      color: isActive ? COLORS.orange.base : completed ? COLORS.gray[400] : COLORS.gray[300],
                      ml: 1,
                    }}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>
              </Tooltip>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};
